/**
 * THE RANKED BAR PANEL — building league, portfolio roll-up, who recorded it.
 *
 * THE ZERO ROW IS THE POINT OF THIS COMPONENT, and it is why this panel is a
 * list of CSS tracks rather than a recharts `<BarChart accessibilityLayer>`.
 *
 * `building` and `portfolio` are zero-filled by the server: a building that
 * logged nothing in the period comes back with a value of 0 rather than being
 * omitted. That row is the most valuable one on the panel — it is the answer to
 * "which of my sites has gone quiet" — and it is exactly the row a stock bar
 * chart loses, because a bar of zero height is not drawn at all.
 *
 * So every row draws its track whether or not it has a value, the fill carries a
 * 3px minimum stub, and the figure is printed beside the label rather than
 * inferred from the bar's length. A zero reads as a deliberate zero.
 *
 * The previous period is a 2px MARKER on the same track, not a second bar.
 * Rows are sorted by value, descending, because a league that is not ranked is a
 * list. `hour_of_day` and `day` deliberately do NOT come here — they read
 * chronologically and are drawn as charts.
 */
import React from 'react';
import { AnalyticsBatchResult, AnalyticsItem } from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable } from '../charts/chart-table-view';
import { Delta } from '../charts/chart-delta';
import { UNRECORDED_FILL } from '../charts/chart-palette';
import { KeyItem, KeyRow, KeySwatch } from './panels.styled';
import {
  BarFill,
  BarLabel,
  BarList,
  BarMarker,
  BarRow,
  BarRowItem,
  BarTrack,
  BarValue,
  BarValueGroup,
} from './panels.styled';
import { asNumber, isUnmatched, readSlot, rowsFromItems, slotState, substitutionCaveat } from './panel-utils';
import { useCompareMeaning } from '../charts/compare-label';

export interface BarPanelProps {
  id: string;
  title: string;
  caveat?: React.ReactNode;
  wide?: boolean;
  state: ChartShellState;
  result?: AnalyticsBatchResult;
  emptyTitle: string;
  emptyText: string;
  /** Present where a row is a filter. Absent elsewhere, and then the rows are
   *  not buttons — a control that looks pressable and does nothing is worse than
   *  a plain row. */
  onSelect?: (item: AnalyticsItem) => void;
  /** The label a row's action announces, e.g. "Show only". */
  selectVerb?: string;
  extraNotes?: string[];
  onExportCsv?: () => void;
}

const sortRows = (items: AnalyticsItem[]): AnalyticsItem[] =>
  [...items].sort((a, b) => asNumber(b) - asNumber(a));

export const BarPanel: React.FC<BarPanelProps> = ({
  id,
  title,
  caveat,
  wide,
  state,
  result,
  emptyTitle,
  emptyText,
  onSelect,
  selectVerb,
  extraNotes,
  onExportCsv,
}) => {
  const compareMeaning = useCompareMeaning();

  const slot = readSlot(result, extraNotes);
  const data = slot.ok;

  /* `last_activity` groups carry an INSTANT, not a count. A bar whose length
     encodes a timestamp is meaningless, so the panel shows the table and says
     nothing it cannot draw honestly. */
  const forceTable = Boolean(data && data.agg === 'max_at');

  const rows = data ? sortRows(data.items) : [];
  const comparing = Boolean(data && data.compareTotal != null);
  // The track's extent is the largest thing that has to fit on it — including a
  // previous-period marker that may be higher than every current value, which is
  // precisely the case where the panel is interesting.
  const ceiling = rows.reduce(
    (max, item) =>
      Math.max(max, asNumber(item), comparing ? item.compareValue ?? 0 : 0),
    0
  );
  const pctOf = (value: number) => (ceiling > 0 ? (value / ceiling) * 100 : 0);
  const anyUnmatched = rows.some(isUnmatched);
  const interactive = Boolean(onSelect);

  return (
    <PanelShell
      id={id}
      title={title}
      caveat={caveat}
      extraCaveat={substitutionCaveat(data)}
      wide={wide}
      state={slotState(state, slot)}
      notes={slot.notes}
      emptyReason={slot.empty ? { title: emptyTitle, text: emptyText } : null}
      forceTable={forceTable}
      onExportCsv={onExportCsv}
      table={
        data ? (
          <ChartTable
            dimensionLabel={title}
            valueLabel={data.label}
            rows={rowsFromItems(data)}
            showCompare={data.compareTotal != null}
          />
        ) : undefined
      }
    >
      {data ? (
        <>
          {comparing || anyUnmatched ? (
            <KeyRow>
              <KeyItem>
                <KeySwatch tone={dataViz.series[0]} aria-hidden="true" />
                This period
              </KeyItem>
              {comparing ? (
                <KeyItem>
                  <KeySwatch tone={palette.inkSoft} aria-hidden="true" />
                  {compareMeaning.label}
                </KeyItem>
              ) : null}
              {anyUnmatched ? (
                <KeyItem>
                  <KeySwatch tone={UNRECORDED_FILL} aria-hidden="true" />
                  Not matched to a name
                </KeyItem>
              ) : null}
            </KeyRow>
          ) : null}

          <BarList data-testid="bar-list">
            {rows.map(item => {
              const value = asNumber(item);
              const unmatched = isUnmatched(item);

              return (
                <BarRowItem key={item.key || item.label}>
                  <BarRow
                    /* A real <button> when the row does something, a plain <div>
                       when it does not — a control that looks pressable and does
                       nothing is worse than a plain row. `type` only exists on
                       the button branch, hence the cast. */
                    as={interactive ? 'button' : 'div'}
                    {...(interactive ? { type: 'button' as const } : {})}
                    interactive={interactive}
                    onClick={interactive ? () => onSelect?.(item) : undefined}
                    aria-label={
                      interactive
                        ? `${selectVerb || 'Show only'} ${item.label}: ${value.toLocaleString()}`
                        : undefined
                    }
                  >
                    <BarLabel>{item.label}</BarLabel>
                    <BarValueGroup>
                      <BarValue>{value.toLocaleString()}</BarValue>
                      <Delta
                        delta={item.delta}
                        compareValue={item.compareValue}
                        likeForLike={data.likeForLike}
                        curSpanMs={data.curSpanMs}
                        prevSpanMs={data.prevSpanMs}
                      />
                    </BarValueGroup>
                    <BarTrack
                      title={`${item.label}: ${value.toLocaleString()}${
                        item.compareValue == null
                          ? ''
                          : ` (was ${item.compareValue.toLocaleString()})`
                      }`}
                    >
                      <BarFill
                        pct={pctOf(value)}
                        tone={unmatched ? UNRECORDED_FILL : dataViz.series[0]}
                      />
                      {comparing && item.compareValue != null ? (
                        <BarMarker pct={pctOf(item.compareValue)} />
                      ) : null}
                    </BarTrack>
                  </BarRow>
                </BarRowItem>
              );
            })}
          </BarList>
        </>
      ) : null}
    </PanelShell>
  );
};
